import React from 'react';
import { Box, Typography } from '@mui/material';
import { usePartnerWorkspace, adoptionRowsFrom } from './partnerPortfolioData';

/**
 * Growth Opportunities > Product Adoption > Solution Adoption — Partner
 * workspace. Built from a reference mockup (a 4-row solution table). The
 * figures and status come from the partner workspace (PW) document's
 * adoption[] (LA-PW-20Oct-02): Reach = schools (or countries) linked to the
 * solution, Active = used in the last 365 days, Adoption Rate = active / reach,
 * Status "Active" at ≥ 50% else "Growth opportunity". A figure the data does
 * not send shows "—". The table itself follows this app's own list-page convention
 * (Box/CSS-grid rows, muted uppercase header, zebra striping) rather than
 * the mockup's own dark navy header.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';

const COLS = 'repeat(5, 1fr) 140px';

const STATUS_STYLES = {
    Active: { bg: 'rgba(22, 101, 52, 0.12)', color: '#166534' },
    'Growth opportunity': { bg: 'rgba(234, 88, 12, 0.12)', color: '#C2410C' },
};

const ColHeader = ({ label }) => (
    <Typography sx={{
        fontSize: '12px', fontWeight: 700, color: MUTED,
        textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
        {label}
    </Typography>
);

const StatusChip = ({ status }) => {
    const style = STATUS_STYLES[status];
    if (!style) return <Typography sx={{ fontSize: '13.5px', color: MUTED }}>—</Typography>;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '72px',
            px: 1.25, py: 0.4, borderRadius: '50px', backgroundColor: style.bg,
        }}>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: style.color, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {status}
            </Typography>
        </Box>
    );
};

const SolutionAdoptionPage = () => {
    const { data } = usePartnerWorkspace();
    const rows = adoptionRowsFrom(data);
    return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
        <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
            Solution Adoption
        </Typography>
        <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 4 }}>
            Track which solutions are active across your portfolio and where adoption can grow.
        </Typography>

        <Box sx={{
            backgroundColor: 'var(--brand-surface, #ffffff)', borderRadius: '16px',
            border: `1px solid ${BORDER}`, p: { xs: 2, sm: '20px 24px' },
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        }}>
            <Box sx={{ minWidth: '760px' }}>
                <Box sx={{
                    display: 'grid', gridTemplateColumns: COLS, gap: 2, px: 2, py: 1.25,
                    backgroundColor: 'var(--brand-bg, #F9FAFB)', borderRadius: '10px', mb: 0.5,
                }}>
                    <ColHeader label="Solution" />
                    <ColHeader label="Reach" />
                    <ColHeader label="Active Learner" />
                    <ColHeader label="Active Organization" />
                    <ColHeader label="Adoption Rate" />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <ColHeader label="Status" />
                    </Box>
                </Box>

                {rows.map((row, i) => (
                    <Box
                        key={row.key}
                        sx={{
                            display: 'grid', gridTemplateColumns: COLS, gap: 2, alignItems: 'center',
                            px: 2, py: 1.75, borderRadius: '8px',
                            backgroundColor: i % 2 !== 0 ? 'var(--brand-bg, #F9FAFB)' : 'var(--brand-surface, #ffffff)',
                            '&:hover': { backgroundColor: 'var(--brand-bg, #F3F4F6)' },
                        }}
                    >
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>
                            {row.solution}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{row.reach}</Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{row.activeLearner}</Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{row.activeOrg}</Typography>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED }}>{row.adoptionRate}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <StatusChip status={row.status} />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
    );
};

export default SolutionAdoptionPage;
